import { Store } from '@ngrx/store';
import { Graphics, Renderer, Sprite, Spritesheet, Texture, Ticker } from 'pixi.js';

import { AppState } from 'app/types';
import User from 'entity/User';

import { COLORS, PLAYER_RADIUS, PLAYER_SIZE } from '../constants';
import { BadgingService } from '../services/badging/badging.service';
import { BrowserService } from '../services/browser.service';
import { ChatService } from '../services/chat.service';
import { EmoteService } from '../services/emote/emote.service';
import { RtcStatsService } from '../services/rtc-stats.service';
import { Player } from '../social-pixi-canvas/player';
import { actions } from '../state/app';
import { delayFor } from 'app/core/utilities/async-utils';
import { ease } from 'pixi-ease';
import Keyboard from '../social-pixi-canvas/keyboard';
import { Keys } from '../social-pixi-canvas/keys';

const DEFAULT_ACCELERATION = 0.4;
const DEFAULT_DECELERATION = 0.95;
const EASING_THRESHOLD = 50;
const EASING_FACTOR_FAR = 0.65;
const EASING_FACTOR_NEAR = 0.85;

export class OnboardingPlayer extends Player {
  public videoMockElement!: HTMLVideoElement;
  public targetX = 0;
  public targetY = 0;
  public limitX = 0;
  public limitY = 0;

  private keyboard!: Keyboard;

  private velocityX = 0;
  private velocityY = 0;
  private acceleration = DEFAULT_ACCELERATION;
  private deceleration = DEFAULT_DECELERATION;

  public constructor({
    badgingService,
    browserService,
    chatService,
    emoteService,
    isOnboardingPlayer,
    isPrimary = false,
    renderer,
    ringColor,
    rtcStatsService,
    showCursorIndicator = true,
    sprites,
    store,
    user,
    videoElement,
    videoEnabled = true,
    x,
    y,
  }: {
    store: Store<AppState>;
    renderer: Renderer;
    user: User;
    isPrimary: boolean;
    browserService: BrowserService;
    chatService: ChatService;
    badgingService: BadgingService;
    rtcStatsService: RtcStatsService;
    videoElement?: HTMLVideoElement;
    videoEnabled?: boolean;
    emoteService: EmoteService;
    x?: number;
    y?: number;
    sprites: Spritesheet;
    ringColor?: string;
    showCursorIndicator?: boolean;
    isOnboardingPlayer?: boolean;
  }) {
    super({
      store,
      renderer,
      user,
      isPrimary,
      browserService,
      chatService,
      badgingService,
      rtcStatsService,
      videoElement,
      videoEnabled,
      emoteService,
      x,
      y,
      sprites,
      ringColor,
      showCursorIndicator,
      isOnboardingPlayer,
    });

    this.keyboard = Keyboard.getInstance();
    this.startMoveInterval();
    this.setPosition(x ?? 0, y ?? 0);
  }

  public setPosition(x: number, y: number) {
    this.targetX = x;
    this.targetY = y;
    // TODO: should the ease time be relative to the distance?
    ease.add(this, { x, y }, { duration: 1000 });
  }

  public setLimitPosition(x: number, y: number) {
    this.limitX = x;
    this.limitY = y;
  }

  public async setMockVideoElement(videoSrc?: string) {
    this.clearVideoElement();

    const height = PLAYER_SIZE;
    const width = PLAYER_SIZE;

    const heightRatio = PLAYER_SIZE / height;
    const widthRatio = PLAYER_SIZE / width;
    const ratio = Math.max(heightRatio, widthRatio);

    const videoSpriteContainer = this.videoSpriteContainer;
    // Video Element -----------------------------------------------------------
    this.videoMockElement = document.createElement('video');

    // Wait for the video to load
    this.videoMockElement.addEventListener('loadeddata', () => {
      this.updateOnboardingVideoState(false);

      const videoTexture = Texture.from(this.videoMockElement);

      const videoSprite = new Sprite(videoTexture);
      videoSprite.anchor.set(0.5, 0.5);
      videoSprite.width = width * ratio * 1.8;
      videoSprite.height = height * ratio;

      // Circle Mask -------------------------------------------------------------
      // the sprite gets scaled based on the image provided so we need this adjusted by same
      const circleMask = new Graphics();
      circleMask.beginFill(COLORS.maskColor);
      circleMask.drawCircle(0, 0, PLAYER_RADIUS);
      circleMask.endFill();

      videoSpriteContainer.mask = circleMask;
      videoSpriteContainer.addChild(circleMask, videoSprite);

      // ---------------------------------------------------------------------------
      this.videoSprite = videoSprite;
      this.videoSpriteContainer = videoSpriteContainer;
    });

    // After video has ended, trigger the function
    this.videoMockElement.addEventListener('ended', () => {
      // Prevent event related to smart badging questions
      if (!this.videoMockElement.src.includes('3-3.mp4')) {
        this.updateOnboardingVideoState(true);
      } else {
        this.clearVideoElement();
      }
    });

    this.updateOnboardingVideoState(false);
    await delayFor(2500);
    this.setupVideoSrc(videoSrc);
  }

  public updateVideoSrc(step: string): void {
    this.clearVideoElement();

    this.updateOnboardingVideoState(false);
    this.videoMockElement.src = `./assets/video/onboarding-experience/${step}.mp4`;
  }

  public clearVideoElement(): void {
    if (this.videoMockElement) {
      this.videoMockElement.pause();
      this.videoMockElement.src = '';
      this.videoMockElement.currentTime = 0;
    }
  }

  // Add movement controls to the video sprite
  // listen to keyboard events to move the video sprite
  public addMovementControls() {
    window.addEventListener('keyup', this.keyboard.onKeyUp.bind(this.keyboard));
    window.addEventListener('keydown', this.keyboard.onKeyDown.bind(this.keyboard));
  }

  public removeMovementControls(): void {
    window.removeEventListener('keyup', this.keyboard.onKeyUp.bind(this.keyboard));
    window.removeEventListener('keydown', this.keyboard.onKeyDown.bind(this.keyboard));
  }

  private startMoveInterval(): void {
    Ticker.shared.add(this.checkKeyDown, this); // Use Pixi's shared ticker
  }

  private checkKeyDown(): void {
    const movementSpeed = 5;
    let moveX = 0;
    let moveY = 0;

    // Check movement keys
    if (this.keyboard.isKeyPressed(Keys.KEY_W) || this.keyboard.isKeyPressed(Keys.KEY_UP)) {
      moveY -= 1;
    }
    if (this.keyboard.isKeyPressed(Keys.KEY_S) || this.keyboard.isKeyPressed(Keys.KEY_DOWN)) {
      moveY += 1;
    }
    if (this.keyboard.isKeyPressed(Keys.KEY_A) || this.keyboard.isKeyPressed(Keys.KEY_LEFT)) {
      moveX -= 1;
    }
    if (this.keyboard.isKeyPressed(Keys.KEY_D) || this.keyboard.isKeyPressed(Keys.KEY_RIGHT)) {
      moveX += 1;
    }

    // Normalize movement for diagonal movement consistency
    const length = Math.hypot(moveX, moveY);
    if (length > 0) {
      moveX = (moveX / length) * movementSpeed;
      moveY = (moveY / length) * movementSpeed;
    }

    // Apply acceleration
    this.velocityX += (moveX - this.velocityX) * this.acceleration;
    this.velocityY += (moveY - this.velocityY) * this.acceleration;

    // Apply deceleration when no input
    this.velocityX *= this.deceleration;
    this.velocityY *= this.deceleration;

    this.targetX += this.velocityX;
    this.targetY += this.velocityY;
  }

  // add this to the shared ticker
  public animate() {
    // Adjust for smoother/slower movement
    const distance = Math.hypot(this.targetX - this.x, this.targetY - this.y);
    const easing = distance > EASING_THRESHOLD ? EASING_FACTOR_FAR : EASING_FACTOR_NEAR;
    // const easing = 0.95;

    // Calculate movement difference
    const dx = this.targetX - this.x;
    const dy = this.targetY - this.y;

    // Stop movement when very close to target (prevents jittering)
    if (Math.abs(dx) < 0.5 && Math.abs(dy) < 0.5) {
      this.x = this.targetX;
      this.y = this.targetY;
      return;
    }

    // Apply smooth movement using interpolation
    this.x += dx * easing;
    this.y += dy * easing;

    // Update position
    this.position.set(this.x, this.y);

    // Handle toggle logic when approaching the limit
    if (this.x >= this.limitX - 100) {
      this.handleToggleGroup(true);
    } else {
      this.handleToggleGroup(false);
    }
  }

  /**
   * Set the target position directly, used for collision resolution and external control
   * @param x New target X position
   * @param y New target Y position
   */
  public setTargetPosition(x: number, y: number): void {
    this.targetX = x;
    this.targetY = y;
  }

  private updateOnboardingVideoState(hasEnded: boolean): void {
    this.store.dispatch(actions.UPDATE_ONBOARDING_VIDEO_STATE({ onboardingVideoEnded: hasEnded }));
  }

  public async playMockVideo(): Promise<void> {
    this.videoMockElement.muted = false;
    await this.videoMockElement.play();
  }

  private setupVideoSrc(videoSrc?: string) {
    if (videoSrc) {
      this.videoMockElement.src = videoSrc;
    }

    this.updateOnboardingVideoState(false);

    this.videoMockElement.loop = false;
    this.videoMockElement.muted = false;
    this.videoMockElement.autoplay = true;
  }

  public handleToggleGroup(showGroupRing: boolean): void {
    this.toggleGroup({
      isInGroup: showGroupRing,
      groupId: null,
      isPrimaryGroup: true,
      warning: false,
    });
  }

  public mockPlayerGroupRing(mainPlayerX: number): void {
    if (mainPlayerX >= this.x - 220) {
      this.handleToggleGroup(true);
    } else {
      this.handleToggleGroup(false);
    }
  }

  public destroy(): void {
    Ticker.shared.remove(this.checkKeyDown, this); // Remove from ticker on destroy
    this.clearVideoElement();
  }
}
